// Partner Referral System Utilities
// This file contains utilities for managing partner referrals and commissions

// Student Status Stages
export const STUDENT_STAGES = [
  "Referred",
  "Seat Booked",
  "Test Provided",
  "Invitation Letter Issued",
  "Admission Letter Confirmed",
  "Visa Approved",
  "Flight Booked & Departed",
];

// Commission Status Types
export const COMMISSION_STATUS = {
  IN_PROGRESS: "In Progress",
  PAID: "Paid",
};

// Commission amount per student
export const COMMISSION_AMOUNT = 20000; // ₹20,000

// Utility function to validate partner credentials
export const validatePartnerCredentials = (partnerId, password) => {
  // Validate partner ID (10-digit phone number)
  if (!/^\d{10}$/.test(partnerId)) {
    return {
      isValid: false,
      message: "Partner ID must be a 10-digit phone number",
    };
  }

  // Validate password format: {phone}@OV2025
  const expectedPassword = `${partnerId}@OV2025`;
  if (password !== expectedPassword) {
    return {
      isValid: false,
      message: "Invalid password",
    };
  }

  return { isValid: true };
};

// Utility function to calculate commission status based on student stage
export const calculateCommissionStatus = (currentStage) => {
  if (currentStage === "Flight Booked & Departed") {
    return COMMISSION_STATUS.PAID;
  }
  return COMMISSION_STATUS.IN_PROGRESS;
};

// Utility function to get stage completion percentage
export const getStageCompletionPercentage = (statusHistory) => {
  const completedStages = statusHistory.filter(
    (stage) => stage.completed
  ).length;
  return Math.round((completedStages / STUDENT_STAGES.length) * 100);
};

// Utility function to format currency in Indian Rupees
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
};

// Utility function to get the next stage in the process
export const getNextStage = (currentStage) => {
  const currentIndex = STUDENT_STAGES.indexOf(currentStage);
  if (currentIndex === -1 || currentIndex === STUDENT_STAGES.length - 1) {
    return null;
  }
  return STUDENT_STAGES[currentIndex + 1];
};

// Utility function to calculate total and pending commissions for a partner
export const calculatePartnerCommissions = (referrals) => {
  let totalCommissionEarned = 0;
  let pendingCommission = 0;
  let studentsWithFlightsBooked = 0;

  referrals.forEach((referral) => {
    if (referral.commissionStatus === COMMISSION_STATUS.PAID) {
      totalCommissionEarned += COMMISSION_AMOUNT;
    }
    // No pending commission - only paid when flight is booked

    if (referral.currentStatus === "Flight Booked & Departed") {
      studentsWithFlightsBooked++;
    }
  });

  return {
    totalStudentsReferred: referrals.length,
    studentsWithFlightsBooked,
    totalCommissionEarned,
    pendingCommission,
  };
};

// Utility function to create a new referral object
export const createNewReferral = (studentData, partnerId) => {
  const now = new Date().toISOString();

  return {
    id: `ref_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    partnerId: partnerId,
    studentName: studentData.studentName,
    studentPhone: studentData.studentPhone,
    studentEmail: studentData.studentEmail,
    notes: studentData.notes || "",
    dateOfReferral: now,
    currentStatus: "Referred",
    commissionStatus: COMMISSION_STATUS.IN_PROGRESS,
    statusHistory: [
      {
        stage: "Referred",
        date: now,
        completed: true,
        updatedBy: "partner",
      },
    ],
  };
};

// Utility function to update student status (admin only)
export const updateStudentStatus = (
  referral,
  newStage,
  updatedBy = "admin"
) => {
  const now = new Date().toISOString();

  // Find the stage in the status history
  const stageIndex = referral.statusHistory.findIndex(
    (s) => s.stage === newStage
  );

  let updatedStatusHistory = [...referral.statusHistory];

  if (stageIndex !== -1) {
    // Update existing stage
    updatedStatusHistory[stageIndex] = {
      ...updatedStatusHistory[stageIndex],
      date: now,
      completed: true,
      updatedBy,
    };
  } else {
    // Add new stage
    updatedStatusHistory.push({
      stage: newStage,
      date: now,
      completed: true,
      updatedBy,
    });
  }

  // Calculate new commission status
  const newCommissionStatus = calculateCommissionStatus(newStage);

  return {
    ...referral,
    currentStatus: newStage,
    commissionStatus: newCommissionStatus,
    statusHistory: updatedStatusHistory,
    lastUpdated: now,
    lastUpdatedBy: updatedBy,
  };
};

// All mock data has been removed - only use real API data
